import LegalLayout from "@/components/LegalLayout";

export default function RefundPolicy() {
  return (
    <LegalLayout
      title="Cancellation & Refund Policy"
      lastUpdated="June 1, 2026"
      testId="page-refund"
      noIndex
    >
      <p>
        This policy explains how cancellations, refunds, and chargebacks work for purchases
        made on Facetra. Because the Service is digital and credits are consumed against AI
        compute costs as soon as you generate a render, our refund policy is designed to be
        fair while accounting for these unrecoverable usage costs.
      </p>

      <h2>1. What You Are Paying For</h2>
      <p>
        Payments on Facetra purchase <strong>render credits</strong> that you spend inside the
        studio (1 credit per generation variant, 1 per refine, 1 per multi-view image, 1 per
        catalog compose, 2 per CAD export). The current pricing is shown on the <a href="/#pricing">Pricing</a>{" "}
        section and at the moment of checkout.
      </p>

      <h2>2. Eligible Refunds</h2>
      <p>You may request a full or partial refund in the following cases:</p>
      <ul>
        <li>
          <strong>Failed transaction with credits not delivered.</strong> If your payment was
          captured by Razorpay but credits did not appear in your wallet within 24 hours, we
          will refund the full amount or credit your account, at your choice.
        </li>
        <li>
          <strong>Unused credits within 7 days of purchase.</strong> If you have not consumed
          any of the credits from a specific pack purchase within 7 days, you can request a
          full refund of that pack.
        </li>
        <li>
          <strong>Service unavailability.</strong> If the Service is unavailable for more than
          48 consecutive hours due to a fault on our side, we will pro-rata credit or refund
          the affected period.
        </li>
        <li>
          <strong>Duplicate charge.</strong> If you were charged twice for the same order, the
          duplicate will be refunded in full automatically once detected, typically within 3
          business days.
        </li>
      </ul>

      <h2>3. Non-Refundable Situations</h2>
      <ul>
        <li>
          <strong>Already-consumed credits.</strong> Once a credit is spent on a generation,
          multi-view, refine, or CAD export, the underlying AI compute cost is paid by us and
          cannot be reversed. We do not refund consumed credits.
        </li>
        <li>
          <strong>Subjective dissatisfaction with AI output.</strong> AI-generated renders are
          conceptual references and may require refinement. We provide the Refine and BOM
          tools to iterate. We do not refund credits used for outputs the user finds
          aesthetically unsatisfactory, provided the Service functioned as designed.
        </li>
        <li>
          <strong>Violation of Terms.</strong> Accounts terminated for violating our{" "}
          <a href="/terms">Terms of Service</a> (abuse, fraud, prohibited content, chargeback
          fraud) are not eligible for refunds of unused balance.
        </li>
        <li>
          <strong>Coupon-granted credits.</strong> Credits granted by promotional coupons
          (e.g. LAUNCH50, NIFT2026) have no cash value and are non-refundable.
        </li>
      </ul>

      <h2>4. How to Request a Refund</h2>
      <p>
        Email <a href="mailto:hello@facetra.studio">hello@facetra.studio</a> with the subject{" "}
        <strong>"Refund Request"</strong> and include:
      </p>
      <ul>
        <li>Your registered email address</li>
        <li>Razorpay <code>order_id</code> or <code>payment_id</code> (found in the
          confirmation email or in the studio under Wallet → Transactions)</li>
        <li>Reason for the refund</li>
      </ul>
      <p>
        We will acknowledge your request within 2 business days and provide a decision within
        7 business days.
      </p>

      <h2>5. Refund Method &amp; Timeline</h2>
      <p>
        Approved refunds are issued back to the original payment method via Razorpay. Typical
        timelines:
      </p>
      <table>
        <thead>
          <tr><th>Method</th><th>Refund Time</th></tr>
        </thead>
        <tbody>
          <tr><td>Credit / Debit Card</td><td>5–7 business days</td></tr>
          <tr><td>UPI</td><td>3–5 business days</td></tr>
          <tr><td>NetBanking</td><td>5–7 business days</td></tr>
          <tr><td>Wallet</td><td>1–3 business days</td></tr>
        </tbody>
      </table>
      <p>
        Razorpay's processing fees on the original transaction are non-refundable in line with
        their standard policy.
      </p>

      <h2>6. Cancellation</h2>
      <p>
        Facetra does not currently operate auto-renewing subscriptions — all credit purchases
        are one-time. You may discontinue use of the Service at any time without cancellation
        fees. Unused credits remain in your wallet while your account is active.
      </p>
      <p>
        If we introduce subscription plans in the future, the cancellation flow will be
        clearly disclosed in-app and you will be able to cancel from the wallet settings.
        Subscription cancellations stop future renewals; the current billing cycle's credits
        remain usable until the end of that cycle.
      </p>

      <h2>7. Chargebacks</h2>
      <p>
        If you believe a charge is fraudulent, please contact us first — most issues can be
        resolved within 48 hours. Initiating a chargeback without first contacting us may
        result in account suspension. Confirmed fraudulent chargebacks may be reported to
        Razorpay and relevant authorities.
      </p>

      <h2>8. Contact</h2>
      <p>
        Refund queries: <a href="mailto:hello@facetra.studio">hello@facetra.studio</a><br />
        Operating hours: Monday–Friday, 10:00–18:00 IST.
      </p>
    </LegalLayout>
  );
}
